import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@coin/shared/core/util-environments';
import { forkJoin, from, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { AuthService } from './auth.service';

@Injectable()
export class TokenInjector implements HttpInterceptor {
  private authService = inject(AuthService);

  private readonly tokenBlacklist = [
    '.s3.eu-central-1.amazonaws.com',
    environment.cmsContentProdBucketCloudfront,
    environment.cmsContentStageBucketCloudfront,
    environment.documentDownloadCloudfront,
    environment.coinAssetsPath,
    environment.coinImagePath
  ];

  private readonly emulationBlacklist = [
    'master/headcount-approval/job-description/generation',
    'master/headcount-approval/job-description/enhancement',
    'master/headcount-approval/job-description/sessions/'
  ];
  private readonly emulationWhitelist = ['document-service/private-documents', 'document-service/direct-documents'];

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    let request = req;

    return forkJoin([from(this.authService.getAccessToken()), from(this.authService.getEmulation())]).pipe(
      switchMap(([accessToken, emulationToken]) => {
        // decode access token and check if url matches one of the audiences
        // as this is not implemented yet.. we validate.. it is not send to an amazonaws service aside from apigateway
        if (!this.urlInTokenBlacklist(request.url)) {
          if (accessToken) {
            request = this.addBearerToken(request, accessToken);
          }

          if (this.shouldSetEmulationToken(request.url, emulationToken)) {
            request = this.setEmulationToken(request, emulationToken);

            if (
              request.url.includes(environment.api.baseUrl) &&
              !request.url.includes(environment.api.letterServiceUrl)
            ) {
              request = this.removeBearerToken(request);
            }
          }
        }

        return next.handle(request);
      })
    );
  }

  private shouldSetEmulationToken(url: string, emulationToken: string): boolean {
    return emulationToken && this.emulationListsAllow(url) && !this.isEmulationTokenRefresh(url);
  }

  private isEmulationTokenRefresh(url: string): boolean {
    return url.endsWith('/oauth/token');
  }

  private addBearerToken(request: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
  }

  private removeBearerToken(request: HttpRequest<unknown>): HttpRequest<unknown> {
    return request.clone({
      setHeaders: {
        Authorization: ''
      }
    });
  }

  private setEmulationToken(request: HttpRequest<unknown>, emulation: string): HttpRequest<unknown> {
    return request.clone({
      setHeaders: {
        EmulationToken: emulation.toString()
      }
    });
  }

  private urlInTokenBlacklist(url: string): boolean {
    return this.tokenBlacklist.some(whiteUrl => url.includes(whiteUrl));
  }

  private emulationListsAllow(url: string): boolean {
    return (
      this.emulationWhitelist.some(whiteUrl => url.includes(whiteUrl)) ||
      !this.emulationBlacklist.some(blackUrl => url.includes(blackUrl))
    );
  }
}
